/**
 * Imports aggregations — pure functions that convert ImportedRow[] into
 * the shapes the existing widgets consume (DefectCount[], time-series
 * points, table rows, KPI summary numbers).
 *
 * These are the bridge between the schema-agnostic row storage and the
 * widget-specific data shapes. Each function checks for the canonical
 * columns it needs (`scrap_pieces`, `good_pieces`, `date`, `main_defect`,
 * `part_number`) and gracefully degrades when columns are missing —
 * a sand-test dataset won't have `scrap_pieces`, for example, so the
 * scrap-KPI aggregator returns null and the widget falls back to its
 * placeholder.
 */

import type { ImportedRow } from "./types";

// ────────────────────────────────────────────────────────────────────
// Generic helpers
// ────────────────────────────────────────────────────────────────────

function toNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isNaN(n) ? null : n;
}

function dateKey(v: unknown): string | null {
  if (typeof v !== "string") return null;
  // ISO timestamp → date portion (YYYY-MM-DD)
  return v.slice(0, 10);
}

/**
 * The day an imported row carries (YYYY-MM-DD): its `date`, else its `sample_date`, or null when it has neither. The
 * one reading of a row's day, for the trends here and for the window of a saved report that reads an import.
 */
export function importedRowDay(row: Pick<ImportedRow, "values">): string | null {
  return dateKey(row.values.date) ?? dateKey(row.values.sample_date);
}

// ────────────────────────────────────────────────────────────────────
// KPI aggregations — for the four headline cards in the Monthly
// Production Review (scrap rate / good / scrap / lots)
// ────────────────────────────────────────────────────────────────────

export interface ScrapSummary {
  goodTotal: number;
  scrapTotal: number;
  /** null when good + scrap === 0 — nobody counted these lots (GATE-SCOPE7). */
  scrapPct: number | null;
  lots: number;
}

export function aggregateScrapSummary(rows: ImportedRow[]): ScrapSummary | null {
  let goodTotal = 0;
  let scrapTotal = 0;
  let lots = 0;
  for (const r of rows) {
    const good = toNum(r.values.good_pieces);
    const scrap = toNum(r.values.scrap_pieces);
    if (good === null && scrap === null) continue;
    goodTotal += good ?? 0;
    scrapTotal += scrap ?? 0;
    lots++;
  }
  if (lots === 0) return null;
  const denom = goodTotal + scrapTotal;
  // GATE-SCOPE7 (2026-08-03): `: 0` here printed "Scrap 0.00%" for a lot set whose
  // pieces nobody counted (an imported sheet with blank good/scrap columns) — the
  // most flattering number the row can carry, asserted as a measurement, and carried
  // into `export-xlsx.ts`'s retained workbook. The file already knew the honest form:
  // `aggregateDailyScrapTrend` and the numeric-series builder below both return
  // `null` for the same missing denominator. This makes the three that did not agree.
  const scrapPct = denom > 0 ? (scrapTotal / denom) * 100 : null;
  return { goodTotal, scrapTotal, scrapPct, lots };
}

/** Returns just the scrap percentage (for KPI widget). */
export function aggregateKpi(
  rows: ImportedRow[],
  key: string,
): { value: number; trend?: Array<{ t: string; v: number }> } | null {
  const summary = aggregateScrapSummary(rows);
  switch (key) {
    case "rejection_rate":
    case "scrap_percentage":
    case "scrap_rate":
      // `scrapPct == null` → the same `null` this function already returns for an
      // unrecognised key, so the KPI card takes its honest empty state instead of
      // rendering 0 % (GATE-SCOPE7). The two nulls mean different things to the
      // caller only in that both refuse to produce a number, which is the point.
      return summary && summary.scrapPct != null
        ? {
            value: Math.round(summary.scrapPct * 100) / 100,
            trend: aggregateDailyScrapTrend(rows).map((p) => ({ t: p.t, v: p.v ?? 0 })),
          }
        : null;
    case "good_pieces":
      return summary ? { value: summary.goodTotal } : null;
    case "scrap_pieces":
      return summary ? { value: summary.scrapTotal } : null;
    case "lots_count":
      return summary ? { value: summary.lots } : null;
    default:
      return null;
  }
}

// ────────────────────────────────────────────────────────────────────
// Defect Pareto — group by main_defect, sum scrap_pieces
// ────────────────────────────────────────────────────────────────────

export interface DefectCountRow {
  defect: string;
  count: number;
  /** Canonical defect id when matchable to the foundry taxonomy. */
  defectId?: string;
}

export function aggregateDefectPareto(rows: ImportedRow[]): DefectCountRow[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const defect = String(r.values.main_defect ?? "").trim();
    if (!defect) continue;
    const scrap = toNum(r.values.scrap_pieces) ?? 0;
    counts.set(defect, (counts.get(defect) ?? 0) + scrap);
  }
  return Array.from(counts.entries())
    .map(([defect, count]) => ({ defect, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 12); // top-N for the chart
}

// ────────────────────────────────────────────────────────────────────
// Daily scrap-rate trend — group by date, compute daily scrap %
// ────────────────────────────────────────────────────────────────────

export interface TimeseriesPoint {
  t: string;
  v: number | null;
}

export function aggregateDailyScrapTrend(rows: ImportedRow[]): TimeseriesPoint[] {
  const perDay = new Map<string, { good: number; scrap: number }>();
  for (const r of rows) {
    const day = importedRowDay(r);
    if (!day) continue;
    const good = toNum(r.values.good_pieces) ?? 0;
    const scrap = toNum(r.values.scrap_pieces) ?? 0;
    const cur = perDay.get(day) ?? { good: 0, scrap: 0 };
    cur.good += good;
    cur.scrap += scrap;
    perDay.set(day, cur);
  }
  return Array.from(perDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, { good, scrap }]) => {
      const denom = good + scrap;
      return {
        t: day,
        v: denom > 0 ? Math.round((scrap / denom) * 10_000) / 100 : null,
      };
    });
}

/**
 * Generic single-series trend for any numeric column (used by sand-test
 * imports rendering against the SPC + time-series widgets).
 */
export function aggregateDailyMean(
  rows: ImportedRow[],
  column: string,
): TimeseriesPoint[] {
  const perDay = new Map<string, { sum: number; n: number }>();
  for (const r of rows) {
    const day = importedRowDay(r);
    if (!day) continue;
    const v = toNum(r.values[column]);
    if (v === null) continue;
    const cur = perDay.get(day) ?? { sum: 0, n: 0 };
    cur.sum += v;
    cur.n += 1;
    perDay.set(day, cur);
  }
  return Array.from(perDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, { sum, n }]) => ({
      t: day,
      v: n > 0 ? Math.round((sum / n) * 1000) / 1000 : null,
    }));
}

// ────────────────────────────────────────────────────────────────────
// Top-N parts by scrap
// ────────────────────────────────────────────────────────────────────

export interface PartScrapRow {
  partNumber: string;
  goodTotal: number;
  scrapTotal: number;
  /** null when good + scrap === 0 (GATE-SCOPE7). */
  scrapPct: number | null;
  lots: number;
  mainDefects: string[]; // most common defects, sorted by count desc
}

export function aggregateTopProblemParts(
  rows: ImportedRow[],
  limit = 20,
): PartScrapRow[] {
  const perPart = new Map<
    string,
    {
      goodTotal: number;
      scrapTotal: number;
      lots: number;
      defectCounts: Map<string, number>;
    }
  >();
  for (const r of rows) {
    const part = String(r.values.part_number ?? "").trim();
    if (!part) continue;
    const good = toNum(r.values.good_pieces) ?? 0;
    const scrap = toNum(r.values.scrap_pieces) ?? 0;
    const defect = String(r.values.main_defect ?? "").trim();
    const cur = perPart.get(part) ?? {
      goodTotal: 0,
      scrapTotal: 0,
      lots: 0,
      defectCounts: new Map(),
    };
    cur.goodTotal += good;
    cur.scrapTotal += scrap;
    cur.lots += 1;
    if (defect) cur.defectCounts.set(defect, (cur.defectCounts.get(defect) ?? 0) + scrap);
    perPart.set(part, cur);
  }
  return Array.from(perPart.entries())
    .map(([partNumber, agg]) => {
      const denom = agg.goodTotal + agg.scrapTotal;
      return {
        partNumber,
        goodTotal: agg.goodTotal,
        scrapTotal: agg.scrapTotal,
        // null, not 0 — see aggregateScrapSummary (GATE-SCOPE7).
        scrapPct: denom > 0 ? (agg.scrapTotal / denom) * 100 : null,
        lots: agg.lots,
        mainDefects: Array.from(agg.defectCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([d]) => d),
      };
    })
    .sort((a, b) => b.scrapTotal - a.scrapTotal)
    .slice(0, limit);
}

// ────────────────────────────────────────────────────────────────────
// Shift / line attribution (used by the cross-tab widgets, later)
// ────────────────────────────────────────────────────────────────────

export interface AttributionRow {
  key: string;
  goodTotal: number;
  scrapTotal: number;
  /** null when good + scrap === 0 (GATE-SCOPE7). */
  scrapPct: number | null;
  lots: number;
}

export function aggregateByCategorical(
  rows: ImportedRow[],
  column: "shift" | "line" | "grade",
): AttributionRow[] {
  const perKey = new Map<string, { good: number; scrap: number; lots: number }>();
  for (const r of rows) {
    const key = String(r.values[column] ?? "").trim();
    if (!key) continue;
    const good = toNum(r.values.good_pieces) ?? 0;
    const scrap = toNum(r.values.scrap_pieces) ?? 0;
    const cur = perKey.get(key) ?? { good: 0, scrap: 0, lots: 0 };
    cur.good += good;
    cur.scrap += scrap;
    cur.lots += 1;
    perKey.set(key, cur);
  }
  return Array.from(perKey.entries())
    .map(([key, { good, scrap, lots }]) => {
      const denom = good + scrap;
      return {
        key,
        goodTotal: good,
        scrapTotal: scrap,
        // null, not 0 — see aggregateScrapSummary (GATE-SCOPE7).
        scrapPct: denom > 0 ? (scrap / denom) * 100 : null,
        lots,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
}
